import {useEffect, useRef, useCallback} from 'react';
import {debounce} from 'lodash';
import type {DebouncedFunc} from 'lodash';

export const useOutsideActionHandler = <T extends HTMLElement>(options: {
  action: () => void;
  enabled?: boolean;
  delay?: number;
}) => {
  const {action, enabled = true, delay = 200} = options;
  const ref = useRef<T>(null);
  const debouncedAction = useRef<DebouncedFunc<() => void> | null>(null);

  const isOutsideElement = useCallback((event: MouseEvent): boolean => {
    return ref.current ? !ref.current.contains(event.target as Node) : false;
  }, []);

  useEffect(() => {
    // Create debounced action for delayed execution
    debouncedAction.current = debounce(action, delay);

    const handleClickOutside = (event: MouseEvent) => {
      if (isOutsideElement(event)) {
        action();
      }
    };

    const handleMouseLeave = () => {
      debouncedAction.current?.();
    };

    const handleMouseEnter = () => {
      debouncedAction.current?.cancel();
    };

    const currentElement = ref.current;

    if (enabled && currentElement) {
      currentElement.addEventListener('mouseleave', handleMouseLeave);
      currentElement.addEventListener('mouseenter', handleMouseEnter);
      window.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      debouncedAction.current?.cancel();
      if (currentElement) {
        currentElement.removeEventListener('mouseleave', handleMouseLeave);
        currentElement.removeEventListener('mouseenter', handleMouseEnter);
      }
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [enabled, action, delay, isOutsideElement]);

  return ref;
};
